import { PlayerSiteLocale } from "constants/player-locales"

/** Mirrors core-api's SeoOverride row. */
export interface SeoOverride {
  id: string
  tenant_id: string
  locale: string
  /** Locale-less route path, normalised: lowercase, no trailing slash ('' = home). */
  path: string

  title: string | null
  description: string | null
  h1: string | null
  canonical: string | null

  robots_index: boolean
  robots_follow: boolean
  robots_advanced: string | null

  og_title: string | null
  og_description: string | null
  og_image: string | null

  /** SEO copy below the page content, collapsible by the visitor. */
  footer_html: string
  footer_collapsed: boolean

  json_ld: unknown[]
  active: boolean

  created_at: string
  updated_at: string
}

export type SeoOverrideInput = Omit<SeoOverride, "id" | "tenant_id" | "created_at" | "updated_at">

/**
 * One built-in route, with the languages it has been overridden in.
 *
 * A route is the same path in every language, so the path is the group key —
 * listing the languages together is what stops one route appearing twelve times.
 */
export interface SeoOverrideGroup {
  path: string
  title: string | null
  locales: string[]
  missing_locales: string[]
  indexed: boolean
  updated_at: string
}

export { PLAYER_LOCALES as SEO_LOCALES } from "constants/player-locales"

export type SeoLocale = PlayerSiteLocale

/**
 * Routes the player frontend renders. Offered as suggestions so an editor does
 * not have to guess a path — a free-text value is still accepted for routes
 * added after this list was written.
 */
export const SEO_KNOWN_PATHS = [
  "",
  "/casino",
  "/sportsbook",
  "/vip",
  "/promotions",
  "/news",
  "/challenges",
  "/games",
  "/originals",
  "/token",
  "/terms-conditions",
  "/privacy-policy",
  "/refund-policy",
  "/responsible-gaming",
  "/kyc-policy",
  "/betting-rules"
] as const

/** One sport the sportsbook routes. `segment` is the URL segment: `soccer-1`. */
export interface SportsbookSport {
  id: number
  slug: string
  name: string
  segment: string
}

/**
 * Where the sportsbook is mounted. A sportsbook-first brand serves it at the
 * root, so its sport pages are /soccer-1 rather than /sportsbook/soccer-1.
 */
export const SPORTSBOOK_PREFIXES = ["/sportsbook", ""] as const
